import { startOfToday } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AptiveIcon, Tabs, Tab, Progress } from '@aptive-env/storybook'
import { Ioffice, Iregion } from 'interface'
import { Card } from '@aptive-env/storybook'
import MiniCalendar from 'components/shared/MiniCalendar'
import { OfficeModal } from 'components/OfficeModal'
import Popover from 'components/shared/Popover'
import { RouteLayersPopoverContent } from 'components/RouteLayersPopoverContent'
import { RouteLayersToggleButton } from 'components/RouteLayersToggleButton'
import { MapTypeChangeButton } from 'components/MapTypeChangeButton'
import { MapTypePopoverContent } from 'components/MapTypePopoverContent'
import Map from 'components/Map'
import { Route, Stats as StatsIcon } from 'icons'
import Stats from 'components/Stats'
import Routes from 'components/Routes'
import styles from './index.module.scss'
import { fetchFleetRoutes, fetchRegions } from 'services/track'
import { fetchUserAvatar } from 'services/calendar'
import { mockupRegions } from 'constants/mockup'
import { EMapType, ICoordinates, IFleetRoute } from 'interface/map'
import { RouteLayer } from 'interface/route'
import { socket } from 'services/socket'
import { MapZoomControl } from 'components/MapZoomControl'
import { calculateTimeLeft } from 'utils/countdownTimer'

const MapView: React.FC = () => {
  const today = startOfToday() // TODO update day from api
  const [fleetRoutes, setFleetRoutes] = useState<any>()
  const [currentDate, setCurrentDate] = useState(today)
  const [regions, setRegions] = useState<Iregion[]>([])
  const [selectedOffice, setSelectedOffice] = useState<Ioffice>(mockupRegions.result[0].offices[0])
  const [routeLayers, setRouteLayers] = useState<RouteLayer[]>(['homes', 'routes', 'route-polygon', 'service-pro', 'service-vehicles'])
  const [isRouteLayersPopoverOpen, setIsRouteLayersPopoverOpen] = useState(false)
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false)
  const [mapCenter, setMapCenter] = useState<ICoordinates | undefined>(mockupRegions.result[0].offices[0].location)
  const [isMapTypePopoverOpen, setIsMapTypePopoverOpen] = useState(false)
  const [mapType, setMapType] = useState<EMapType>(EMapType.TRAFFIC)
  const [mapZoom, setMapZoom] = useState(11)

  const [timeLeft, setTimeLeft] = useState<string>(calculateTimeLeft(currentDate, selectedOffice.timezone?.timezone))

  const isRoutesStarted = useMemo(() => {
    return timeLeft === ''
  }, [timeLeft])

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(currentDate, selectedOffice.timezone?.timezone))
    }, 1000)

    return () => clearInterval(timer)
  }, [currentDate, selectedOffice])

  useEffect(() => {
    fetchFleetRoutes(selectedOffice.id, currentDate)
      .then(async(routers) => {
        // fetch all service pro avatars
        const promises: any[] = []
        routers?.routes.forEach((route: IFleetRoute) => {
          promises.push(fetchUserAvatar(route.external_id))
        })

        await Promise.all(promises)
          .then((avatars) => {
            routers?.routes.forEach((route: IFleetRoute, index: number) => {
              route.avatar = avatars[index]?._metadata.success ? avatars[index].result.avatarBase64 : undefined
            })
          })
          .catch((error) => console.log(error))

        setFleetRoutes(routers)
      })

    socket.on('connect', () => {
      console.log('Connected')

      // Request to join a room
      socket.emit('join', 'aro-service')
      console.log('Joined to room: aro-service')
    })

    socket.on('disconnect', () => {
      console.log('Disconnected')
    })

    socket.on('connect_error', () => {
      console.log('Connection Error')
      console.log('Attempting reconnect...')
      setTimeout(() => {
        socket.connect()
      }, 1000)
    })

    socket.on(`track-office-${selectedOffice.id}`, (...args: any[]) => {
      const [data] = args
      console.log('Event Received:', 'aro-service', `tracking-office-${selectedOffice.id}`, data)

      // Update the map with the new data
      const tempFleetRoutes = { ...fleetRoutes }
      tempFleetRoutes['routes'].forEach((route: IFleetRoute) => {
        data['routes'].forEach((newRoute: IFleetRoute) => {
          if (route.id === newRoute.id) {
            route.actual_stats = newRoute.actual_stats
            route.tracking_data = newRoute.tracking_data
            route.statistics = newRoute.statistics
          }
        })
      })
      tempFleetRoutes['summary'] = data['summary']
      setFleetRoutes(tempFleetRoutes)
    })

    return () => {
      socket.off('connect_error')
      socket.off('connect')
      socket.off('disconnect')
      socket.off(`track-office-${selectedOffice.id}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, selectedOffice])

  useEffect(() => {
    fetchRegions().then(
      (regions: any) => {
        setRegions(regions)
      }
    )
  }, [])

  const handleDateChange = useCallback((date: Date) => setCurrentDate(date), [])

  const openOfficeSelectionModal = () => {
    setIsOfficeModalOpen(true)
  }

  const closeOfficeSelectionModal = () => {
    setIsOfficeModalOpen(false)
  }

  const handleClickRouteLayersToggleButton = () => {
    setIsMapTypePopoverOpen(false)
    setIsRouteLayersPopoverOpen((prevOpen) => !prevOpen)
  }

  const toggleRouteLayer = (layer: RouteLayer) => {
    if (routeLayers.includes(layer)) {
      setRouteLayers(routeLayers.filter((item) => item !== layer))
    } else {
      setRouteLayers([...routeLayers, layer])
    }
  }

  const handleChangeOffice = (office: Ioffice) => {
    setSelectedOffice(office)
    closeOfficeSelectionModal()
    setMapCenter(office.location)
  }

  const handleClickMapTypeChangeButton = (() => {
    setIsRouteLayersPopoverOpen(false)
    setIsMapTypePopoverOpen(!isMapTypePopoverOpen)
  })

  const handleChangeMapType = (type: EMapType) => {
    setMapType(type)
    setIsMapTypePopoverOpen(false)
  }

  const handleZoom = (type: 'in' | 'out') => {
    if (type === 'in') {
      setMapZoom(mapZoom + 1)
    }
    if (type === 'out' && mapZoom > 0) {
      setMapZoom(mapZoom - 1)
    }
  }

  const [currentTab, setCurrentTab] = useState(0)

  return (
    <div className="grow relative px-[32px] py-[24px]">
      <div className="h-[calc(100%-48px)] absolute z-10">
        <Card className="h-[calc(100%-34px)] min-w-[375px] bg-ui-snow mx-[15px] my-[17px] rounded-lg shadow-med flex flex-col">
          <MiniCalendar
            className="bg-white border-b border-ui-gray-100 rounded-t-lg"
            currentDate={currentDate}
            view="week"
            onChange={handleDateChange}
          />
          <div className="pl-[24px] pr-[8px] py-[16px] bg-ui-gray-50 border-b border-ui-gray-100 text-ui-gray-600 flex items-center justify-between">
            <div className="flex flex-col">
              <div className="text-small mb-2">Office: <span>{selectedOffice?.region} /</span></div>
              <div className="text-bodyBold font-bold">{selectedOffice?.name}</div>
            </div>
            <div className="flex items-center justify-center w-[64px] h-[40px] cursor-pointer" onClick={openOfficeSelectionModal}>
              <AptiveIcon className="w-[24px] h-[24px] stroke-ui-gray-600 fill-none" icon="globeAlt" />
              <div className="w-[24px] h-[24px] flex flex-col items-center">
                <AptiveIcon className="w-[16px] h-[14px] stroke-ui-gray-600 fill-none" icon="chevronUp" />
                <AptiveIcon className="w-[16px] h-[14px] stroke-ui-gray-600 fill-none" icon="chevronDown" />
              </div>
            </div>
          </div>
          {!isRoutesStarted && (
            <div className="h-[62px] flex-none px-6 bg-ui-gray-200">
              <div className="text-[14px] font-normal mt-3 text-text-default">
                Routes start in:
              </div>
              <Progress
                className="bg-white mt-[4px] [&>.progresslabel]:top-[-20px] [&>.progressvalue]:h-[10px]"
                value={timeLeft}
              />
            </div>
          )}
          <OfficeModal
            open={isOfficeModalOpen}
            className="w-[658px] min-h-599"
            selectedOffice={selectedOffice}
            regions={regions}
            onSelectOffice={(office) => handleChangeOffice(office)}
            onClose={closeOfficeSelectionModal}
            center={selectedOffice.location}
          />
          <div className={`${isRoutesStarted ? 'h-[calc(100%-201px)]' : 'h-[calc(100%-201px-62px)]'} flex flex-col p-4 gap-4`}>
            <Tabs className="p-1 pb-[3px] border-b border-[#E0E3E8]" value={currentTab} onChange={setCurrentTab}>
              <Tab className={`h-[40px] flex flex-1 justify-center items-center gap-2 ${currentTab === 0 ? styles.activeTab : ''}`} value={0}>
                <Route className={`${currentTab === 0 ? styles.activeSvg : ''}`} />
                <span className="font-inter font-bold text-xs">Routes</span>
              </Tab>
              <Tab className={`h-[40px] flex flex-1 justify-center items-center gap-2 ${currentTab === 1 ? styles.activeTab : ''}`} value={1}>
                <StatsIcon className={`${currentTab === 1 ? styles.activeSvg : ''}`} />
                <span className="font-inter font-bold text-xs">Stats</span>
              </Tab>
            </Tabs>

            {fleetRoutes && (
              currentTab === 0
                ? <Routes items={fleetRoutes['routes']} isStarted={isRoutesStarted} />
                : <Stats items={fleetRoutes['summary']} />
            )}
          </div>
        </Card>
      </div>
      <div className="absolute z-10 right-[32px] mx-[15px] my-[17px]">
        <Popover
          open={isRouteLayersPopoverOpen}
          position="left"
          content={
            <RouteLayersPopoverContent
              routeLayers={routeLayers}
              onClickClose={() => setIsRouteLayersPopoverOpen(false)}
              onClickOption={toggleRouteLayer}
            />
          }
          onClickOutside={() => setIsRouteLayersPopoverOpen(false)}
        >
          <RouteLayersToggleButton
            onClick={handleClickRouteLayersToggleButton}
          />
        </Popover>
      </div>
      <div className="absolute z-10 right-[32px] top-[112px] mx-[15px] my-[17px]">
        <Popover
          open={isMapTypePopoverOpen}
          position="left"
          content={
            <MapTypePopoverContent
              type={mapType}
              onChangeType={handleChangeMapType}
            />
          }
          onClickOutside={() => setIsMapTypePopoverOpen(false)}
        >
          <MapTypeChangeButton
            type={mapType}
            onClick={handleClickMapTypeChangeButton}
          />
        </Popover>
      </div>
      <div className="absolute z-10 right-[54px] top-[208px] mx-[15px] my-[17px]">
        <MapZoomControl
          onZoom={handleZoom}
        />
      </div>
      <Map
        mapType={mapType}
        mapData={fleetRoutes}
        routeLayers={routeLayers}
        center={mapCenter}
        zoom={mapZoom}
      />
    </div>
  )
}

export default MapView
